// Parsing of scanned (OCR) supplier invoices. Everything supplier specific comes from the
// supplier record (identifier, number pattern, date order, item section markers and item pattern),
// so supporting a new supplier only requires filling its fields.

const IDENTIFIER_ROWS = 7;

function toLines(rawText) {
  return String(rawText || '')
    .replace(/\r/g, '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

function normalizeScanText(rawText) {
  return toLines(rawText).join('\n');
}

function splitIdentifier(value) {
  return String(value || '')
    .split('|')
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

function alnum(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function identifierTokenMatchesLine(token, line) {
  if (token.indexOf('@') !== -1) {
    const wanted = token.toLowerCase().replace(/\s+/g, '');
    return line.toLowerCase().replace(/\s+/g, '').replace(/,/g, '.').indexOf(wanted) !== -1;
  }

  const digits = token.replace(/\D/g, '');
  const isPhone = digits.length >= 7 && token.replace(/[\s().+-]/g, '').length === digits.length;
  if (isPhone) {
    return line.replace(/\D/g, '').indexOf(digits) !== -1;
  }

  const wanted = alnum(token);
  return wanted !== '' && alnum(line).indexOf(wanted) !== -1;
}

// Returns the number of identifier tokens found in the first rows of the text.
function scoreSupplierIdentifier(lines, supplier, rows = IDENTIFIER_ROWS) {
  const head = lines.slice(0, rows);
  return splitIdentifier(supplier.supplier_invoice_identifier)
    .filter((token) => head.some((line) => identifierTokenMatchesLine(token, line)))
    .length;
}

function detectSupplier(rawText, suppliers) {
  const lines = toLines(rawText);
  let best = null;
  let bestScore = 0;

  (suppliers || []).forEach((supplier) => {
    const score = scoreSupplierIdentifier(lines, supplier);
    if (score > bestScore) {
      best = supplier;
      bestScore = score;
    }
  });

  return best;
}

function extractNumber(text, pattern) {
  if (!pattern) {
    return '';
  }
  const match = new RegExp(pattern, 'm').exec(text);
  if (!match) {
    return '';
  }
  const value = (match.groups && match.groups.number) || match[1] || match[0];
  return String(value).trim();
}

function pad2(value) {
  return String(value).padStart(2, '0');
}

// Returns the first plausible date of the text as YYYY-MM-DD.
function extractDate(text, order) {
  const regex = /\b(\d{1,4})[/-](\d{1,2})[/-](\d{1,4})\b/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const a = parseInt(match[1], 10);
    const b = parseInt(match[2], 10);
    const c = parseInt(match[3], 10);
    let year;
    let month;
    let day;

    if (order === 'dmy') {
      [day, month, year] = [a, b, c];
    } else if (order === 'ymd') {
      [year, month, day] = [a, b, c];
    } else {
      [month, day, year] = [a, b, c];
    }

    if (year < 100) {
      year += 2000;
    }
    if (year >= 2000 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${pad2(month)}-${pad2(day)}`;
    }
  }

  return '';
}

function extractItemsSection(text, startPattern, endPattern) {
  let begin = 0;
  let section = text;

  if (startPattern) {
    const start = new RegExp(startPattern, 'im').exec(text);
    if (start) {
      begin = start.index + start[0].length;
      section = text.slice(begin);
    }
  }

  if (endPattern) {
    const end = new RegExp(endPattern, 'im').exec(section);
    if (end) {
      section = section.slice(0, end.index);
    }
  }

  return section;
}

function parseAmount(value) {
  const cleaned = String(value || '').replace(/[^0-9,.-]/g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function extractItems(section, itemPattern) {
  if (!itemPattern) {
    return [];
  }

  const regex = new RegExp(itemPattern, 'gm');
  const matches = [];
  let match;

  while ((match = regex.exec(section)) !== null) {
    if (match[0] === '') {
      regex.lastIndex++;
      continue;
    }
    matches.push(match);
  }

  // The raw text of a row runs from the start of its first line to the start of the next row,
  // so values the template does not capture (prices, totals) are still shown.
  const lineStart = (index) => section.lastIndexOf('\n', index - 1) + 1;
  const items = [];

  matches.forEach((current, position) => {
    const groups = current.groups || {};
    const previous = matches[position - 1];
    const next = matches[position + 1];
    const currentLineStart = lineStart(current.index);
    // Several rows may sit on the same line when the scan lost the line breaks
    const sharesLineWithPrevious = previous && previous.index + previous[0].length > currentLineStart;
    const rawStart = sharesLineWithPrevious ? current.index : currentLineStart;
    let rawEnd = section.length;
    if (next) {
      const nextSharesLine = next.index < current.index + current[0].length
        || section.indexOf('\n', current.index + current[0].length) === -1
        || section.indexOf('\n', current.index + current[0].length) > next.index;
      rawEnd = nextSharesLine ? next.index : lineStart(next.index);
    }
    const rawText = section.slice(rawStart, Math.max(rawEnd, current.index + current[0].length));

    let qty = parseAmount(groups.qty);
    let qtyEstimated = false;

    if (!(qty > 0)) {
      const price = parseAmount(groups.price);
      const total = parseAmount(groups.total);
      qty = price > 0 && total > 0 ? Math.round(total / price) : 0;
      qtyEstimated = true;
      if (!(qty > 0)) {
        qty = 1;
      }
    }

    items.push({
      // Part numbers are always stored without spaces or dashes
      part_number: cleanText(groups.part).replace(/[\s-]+/g, ''),
      description: cleanText(groups.desc),
      qty,
      qty_estimated: qtyEstimated,
      raw: rawText.split('\n').map((line) => line.trim()).filter(Boolean).join(' | '),
    });
  });

  return items;
}

function parseSupplierInvoice(rawText, supplier) {
  const text = normalizeScanText(rawText);
  const errors = [];
  let number = '';
  let items = [];

  try {
    number = extractNumber(text, supplier.supplier_invoice_number_pattern);
  } catch (e) {
    errors.push('Le motif du numéro de facture du fournisseur est invalide.');
  }

  try {
    items = extractItems(
      extractItemsSection(text, supplier.supplier_invoice_items_start, supplier.supplier_invoice_items_end),
      supplier.supplier_invoice_item_pattern
    );
  } catch (e) {
    errors.push('Le motif des lignes de pièces du fournisseur est invalide.');
  }

  return {
    supplier_id: supplier.supplier_id,
    number,
    date: extractDate(text, supplier.supplier_invoice_date_order || 'mdy'),
    items,
    errors,
  };
}

module.exports = {
  IDENTIFIER_ROWS,
  normalizeScanText,
  detectSupplier,
  scoreSupplierIdentifier,
  extractDate,
  parseSupplierInvoice,
};
